import React, { createContext, useContext, useState, useEffect } from 'react';
import client from '../api/client';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser]                     = useState(null);
  const [org, setOrg]                       = useState(null);
  const [permissions, setPermissions]       = useState([]);
  const [permDepts, setPermDepts]           = useState([]);
  const [isAdmin, setIsAdmin]               = useState(false);
  const [displayName, setDisplayName]       = useState(null);
  const [loading, setLoading]               = useState(true);
  const [permissionsLoaded, setPermsLoaded] = useState(false);

  const fetchPermissions = async (currentOrg) => {
    try {
      const res = await client.get('/me/permissions');
      setPermissions(res.data.permissions  || []);
      setPermDepts(res.data.departments    || []);
      setIsAdmin(res.data.is_admin         || false);
      setDisplayName(res.data.display_name || null);
      if (res.data.org_slug) {
        const base = currentOrg || JSON.parse(localStorage.getItem('org') || '{}');
        const updated = { ...base, slug: res.data.org_slug };
        localStorage.setItem('org', JSON.stringify(updated));
        setOrg(updated);
      }
    } catch (err) {
      console.error('Failed to fetch permissions', err);
    } finally {
      setPermsLoaded(true);
    }
  };

  useEffect(() => {
    const token      = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedOrg  = localStorage.getItem('org');
    if (token && storedUser && storedOrg) {
      const parsedUser = JSON.parse(storedUser);
      const parsedOrg  = JSON.parse(storedOrg);
      setUser(parsedUser);
      setOrg(parsedOrg);
      fetchPermissions(parsedOrg);
    } else {
      setPermsLoaded(true);
    }
    setLoading(false);
  }, []);

  const login = async (slug, email, password) => {
    const res = await client.post('/auth/login', { slug, email, password });
    localStorage.setItem('token',        res.data.token);
    localStorage.setItem('refreshToken', res.data.refreshToken);
    localStorage.setItem('user',         JSON.stringify(res.data.user));
    localStorage.setItem('org',          JSON.stringify(res.data.org));
    setUser(res.data.user);
    setOrg(res.data.org);
    await fetchPermissions(res.data.org);
    return res.data;
  };

  const logout = () => {
    localStorage.clear();
    setUser(null);
    setOrg(null);
    setPermissions([]);
    setPermDepts([]);
    setIsAdmin(false);
    setDisplayName(null);
    setPermsLoaded(false);
    window.location.href = '/';
  };

  const updateOrg = (updates) => {
    const updated = { ...org, ...updates };
    localStorage.setItem('org', JSON.stringify(updated));
    setOrg(updated);
  };

  const can = (resource) => {
    if (!resource) return true;
    if (isAdmin) return true;
    return permissions.includes(resource);
  };

  const canViewDept = (deptId) => {
    if (isAdmin) return true;
    return permDepts.some(d => d.id === deptId);
  };

  const getRoleLabel = () => {
    if (displayName) return displayName;
    if (isAdmin) return 'Admin';
    if (user?.role === 'manager') return 'Manager';
    return user?.role || '';
  };

  return (
    <AuthContext.Provider value={{
      user, org, login, logout, loading, updateOrg,
      permissions, permDepts, isAdmin, can, canViewDept,
      permissionsLoaded, displayName, getRoleLabel,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);